/*
 * Pipeline 7.2.2 - Omniflow image-only variant colour fix
 *
 * IMPORTANT:
 * This file ONLY changes product gallery filtering.
 * It does NOT change:
 * - size/colour option combinations
 * - variant availability
 * - inventory logic
 * - prices
 * - selected variant logic
 * - add-to-cart behaviour
 */

(function () {
  'use strict';

  var DATA_SELECTOR = '[data-omniflow-image-map]';
  var instances = new WeakMap();

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function slugify(value) {
    var string = normalize(value);

    if (typeof string.normalize === 'function') {
      string = string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    return string
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getFilename(src) {
    if (!src) return '';

    try {
      var clean = String(src).split('?')[0];
      var filename = clean.substring(clean.lastIndexOf('/') + 1);
      return decodeURIComponent(filename).toLowerCase();
    } catch (error) {
      return String(src).toLowerCase();
    }
  }

  function getProductRoot(element) {
    return element.closest('[data-section-id][data-section-type="product"]');
  }

  function findColorIndex(data) {
    var accepted = ['colore', 'color', 'colour'];

    for (var i = 0; i < data.options.length; i++) {
      if (accepted.indexOf(normalize(data.options[i])) !== -1) {
        return i;
      }
    }

    return -1;
  }

  function buildMediaSkuMap(data) {
    var map = {};

    var skus = data.variants
      .map(function (variant) {
        return String(variant.sku || '').trim();
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return b.length - a.length;
      });

    data.media.forEach(function (media) {
      if (media.type !== 'image') return;

      var filename = getFilename(media.src);

      var sku = skus.find(function (candidate) {
        var normalizedSku = normalize(candidate);

        return (
          filename.indexOf(normalizedSku + '-') === 0 ||
          filename.indexOf(normalizedSku + '_') === 0 ||
          filename === normalizedSku ||
          filename.indexOf(normalizedSku + '.') === 0
        );
      });

      if (sku) {
        map[String(media.id)] = sku;
      }
    });

    return map;
  }

  function buildRepresentativeSkuByColor(data, colorIndex, mediaSkuMap) {
    var result = {};
    var mediaSkus = Object.keys(mediaSkuMap).map(function (mediaId) {
      return String(mediaSkuMap[mediaId]);
    });

    data.variants.forEach(function (variant) {
      var color = variant.options[colorIndex];
      var sku = String(variant.sku || '');

      if (!color || !sku || result[color]) return;

      if (mediaSkus.indexOf(sku) !== -1) {
        result[color] = sku;
      }
    });

    return result;
  }

  function getMediaSuffix(src, sku) {
    var filename = getFilename(src);
    var normalizedSku = normalize(sku);

    if (!filename || !normalizedSku) return '';

    if (filename.indexOf(normalizedSku) !== 0) return '';

    return filename.substring(normalizedSku.length);
  }

  function buildMediaRedirectMap(data, colorIndex, mediaSkuMap, representativeSkuByColor) {
    var redirect = {};
    var mediaBySkuAndSuffix = {};

    data.media.forEach(function (media) {
      if (media.type !== 'image') return;

      var mediaId = String(media.id);
      var sku = mediaSkuMap[mediaId];
      if (!sku) return;

      var suffix = getMediaSuffix(media.src, sku);
      var key = String(sku) + '||' + suffix;
      mediaBySkuAndSuffix[key] = mediaId;
    });

    data.media.forEach(function (media) {
      if (media.type !== 'image') return;

      var mediaId = String(media.id);
      var sku = mediaSkuMap[mediaId];
      if (!sku) return;

      var variant = data.variants.find(function (item) {
        return String(item.sku || '') === String(sku);
      });
      if (!variant) return;

      var color = variant.options[colorIndex];
      var representativeSku = representativeSkuByColor[color];
      if (!representativeSku) return;

      if (String(representativeSku) === String(sku)) {
        redirect[mediaId] = mediaId;
        return;
      }

      var suffix = getMediaSuffix(media.src, sku);
      var equivalentId = mediaBySkuAndSuffix[
        String(representativeSku) + '||' + suffix
      ];

      /*
       * Prefer the equivalent shot (e.g. -4 -> -4) from the representative
       * SKU. If that shot does not exist, fall back to the first image from
       * the representative SKU so Pipeline never navigates to a hidden cell.
       */
      if (!equivalentId) {
        var fallback = data.media.find(function (candidate) {
          return (
            candidate.type === 'image' &&
            String(mediaSkuMap[String(candidate.id)] || '') === String(representativeSku)
          );
        });
        equivalentId = fallback ? String(fallback.id) : '';
      }

      if (equivalentId) {
        redirect[mediaId] = String(equivalentId);
      }
    });

    return redirect;
  }

  function assignGalleryGroups(instance) {
    var root = instance.root;

    Object.keys(instance.mediaSkuMap).forEach(function (mediaId) {
      var sku = String(instance.mediaSkuMap[mediaId]);

      var variant = instance.data.variants.find(function (item) {
        return String(item.sku || '') === sku;
      });

      if (!variant) return;

      var color = variant.options[instance.colorIndex];
      var representativeSku = instance.representativeSkuByColor[color];

      /*
       * The management system duplicates the same colour gallery for each size.
       * Keep one SKU's image set per colour and exclude the duplicated sets.
       */
      var filterValue =
        String(representativeSku || '') === sku
          ? 'omniflow-' + slugify(color)
          : 'omniflow-image-duplicate';

      root
        .querySelectorAll('[data-media-id="' + mediaId + '"]')
        .forEach(function (node) {
          node.setAttribute('data-image-filter', filterValue);
        });

      root
        .querySelectorAll('[data-media-select="' + mediaId + '"]')
        .forEach(function (node) {
          node.setAttribute('data-image-filter', filterValue);
        });
    });
  }

  function getVariantById(instance, id) {
    return instance.data.variants.find(function (variant) {
      return String(variant.id) === String(id);
    });
  }

  function getFilterForVariant(instance, variantId) {
    var variant = getVariantById(instance, variantId);
    if (!variant) return '';

    var color = variant.options[instance.colorIndex];
    if (!color) return '';

    return 'omniflow-' + slugify(color);
  }

  function getFilterForColor(color) {
    if (!color) return '';
    return 'omniflow-' + slugify(color);
  }

  function getSelectedColor(instance) {
    var position = instance.colorIndex + 1;
    var wrapper = instance.root.querySelector(
      '[data-option-position="' + position + '"]'
    );

    if (!wrapper) return '';

    var checked = wrapper.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value || '';

    var hidden = wrapper.querySelector('[data-single-option-selector]');
    if (hidden) return hidden.value || '';

    return '';
  }

  function showFilter(instance, filter) {
    if (!filter) return;

    var root = instance.root;

    root.querySelectorAll('[data-media-slide]').forEach(function (slide) {
      var value = slide.getAttribute('data-image-filter') || '';

      if (value === '' || value === filter) {
        slide.classList.remove('hide');
      } else {
        slide.classList.add('hide');
      }
    });

    root.querySelectorAll('[data-slideshow-thumbnail]').forEach(function (thumb) {
      var value = thumb.getAttribute('data-image-filter') || '';

      if (value === '' || value === filter) {
        thumb.classList.remove('hide');
      } else {
        thumb.classList.add('hide');
      }
    });

    /* Refresh Pipeline's existing sliders after changing visible cells. */
    var slider = root.querySelector('[data-product-slideshow]');
    if (slider && slider.classList.contains('flickity-enabled')) {
      try {
        if (window.FlickityFade && typeof window.FlickityFade.data === 'function') {
          var slideshow = window.FlickityFade.data(slider);
          if (slideshow && typeof slideshow.reloadCells === 'function') {
            slideshow.reloadCells();
          }
        }
      } catch (error) {}
    }

    var thumbSlider = root.querySelector('[data-product-thumbs]');
    if (thumbSlider && thumbSlider.classList.contains('flickity-enabled')) {
      try {
        if (window.FlickitySync && typeof window.FlickitySync.data === 'function') {
          var thumbs = window.FlickitySync.data(thumbSlider);
          if (thumbs && typeof thumbs.reloadCells === 'function') {
            thumbs.reloadCells();
          }
        }
      } catch (error) {}
    }
  }

  function init(script) {
    var root = getProductRoot(script);
    if (!root || instances.has(root)) return;

    var data;

    try {
      data = JSON.parse(script.textContent);
    } catch (error) {
      return;
    }

    if (!data || !data.variants || !data.media) return;

    var colorIndex = findColorIndex(data);
    if (colorIndex === -1) return;

    var mediaSkuMap = buildMediaSkuMap(data);

    var representativeSkuByColor = buildRepresentativeSkuByColor(
      data,
      colorIndex,
      mediaSkuMap
    );

    var instance = {
      root: root,
      data: data,
      colorIndex: colorIndex,
      mediaSkuMap: mediaSkuMap,
      representativeSkuByColor: representativeSkuByColor,
      mediaRedirectMap: buildMediaRedirectMap(
        data,
        colorIndex,
        mediaSkuMap,
        representativeSkuByColor
      )
    };

    instances.set(root, instance);
    assignGalleryGroups(instance);

    /*
     * Initial page load: prefer the colour currently selected in the UI.
     * This also works when the selected colour has no in-stock variant.
     */
    var selectedColor = getSelectedColor(instance);
    if (selectedColor) {
      showFilter(instance, getFilterForColor(selectedColor));
    } else {
      var idInput = root.querySelector('product-form input[name="id"]');
      if (idInput && idInput.value) {
        showFilter(instance, getFilterForVariant(instance, idInput.value));
      }
    }
  }

  function initAll() {
    document.querySelectorAll(DATA_SELECTOR).forEach(init);
  }

  /*
   * Image-only fallback for unavailable colour combinations.
   * Pipeline may not emit a usable variant when the selected combination is
   * unavailable. We therefore listen to the colour option itself and update
   * ONLY the gallery filter, without touching variant selection/availability.
   */
  document.addEventListener('change', function (event) {
    var root = getProductRoot(event.target);
    if (!root) return;

    var instance = instances.get(root);
    if (!instance) {
      var script = root.querySelector(DATA_SELECTOR);
      if (script) init(script);
      instance = instances.get(root);
    }
    if (!instance) return;

    var colorPosition = instance.colorIndex + 1;
    var colorWrapper = event.target.closest(
      '[data-option-position="' + colorPosition + '"]'
    );
    if (!colorWrapper) return;

    var color = '';

    if (event.target.matches('input[type="radio"]')) {
      color = event.target.value || '';
    } else if (event.target.matches('[data-single-option-selector]')) {
      color = event.target.value || '';
    }

    if (!color) {
      color = getSelectedColor(instance);
    }

    if (!color) return;

    window.requestAnimationFrame(function () {
      showFilter(instance, getFilterForColor(color));
    });
  });

  /* Pipeline popout selectors can update the hidden input programmatically. */
  document.addEventListener('click', function (event) {
    var option = event.target.closest('[data-popout-option]');
    if (!option) return;

    var root = getProductRoot(option);
    if (!root) return;

    var instance = instances.get(root);
    if (!instance) return;

    var colorPosition = instance.colorIndex + 1;
    var wrapper = option.closest(
      '[data-option-position="' + colorPosition + '"]'
    );
    if (!wrapper) return;

    var color = option.getAttribute('data-value') || '';
    if (!color) return;

    window.requestAnimationFrame(function () {
      showFilter(instance, getFilterForColor(color));
    });
  });

  /*
   * Pipeline changes the slideshow to variant.featured_media BEFORE it emits
   * theme:variant:change. For duplicated galleries this may point to a media
   * cell that we intentionally hide. In capture phase, redirect that request
   * to the equivalent visible media from the representative SKU of the same
   * colour. This changes ONLY the gallery target, never the selected variant.
   */
  document.addEventListener(
    'theme:image:change',
    function (event) {
      if (!event.detail || event.detail.id == null) return;

      var root = getProductRoot(event.target);
      if (!root) return;

      var instance = instances.get(root);
      if (!instance) {
        var script = root.querySelector(DATA_SELECTOR);
        if (script) init(script);
        instance = instances.get(root);
      }
      if (!instance) return;

      var requestedId = String(event.detail.id);
      var visibleId = instance.mediaRedirectMap[requestedId];

      if (visibleId && String(visibleId) !== requestedId) {
        event.detail.id = visibleId;
      }
    },
    true
  );

  /*
   * Capture phase: before Pipeline's own GroupVariantImages listener runs,
   * give the current variant the synthetic ALT filter expected by Pipeline.
   * No variant selection is changed here.
   */
  document.addEventListener(
    'theme:variant:change',
    function (event) {
      if (!event.detail || !event.detail.variant) return;

      var root = getProductRoot(event.target);
      if (!root) return;

      var instance = instances.get(root);
      if (!instance) {
        var script = root.querySelector(DATA_SELECTOR);
        if (script) init(script);
        instance = instances.get(root);
      }
      if (!instance) return;

      var filter = getFilterForVariant(instance, event.detail.variant.id);
      if (!filter) return;

      if (!event.detail.variant.featured_image) {
        event.detail.variant.featured_image = {};
      }

      event.detail.variant.featured_image.alt = 'Omniflow#' + filter;
    },
    true
  );

  /*
   * After Pipeline has processed the variant change, enforce only the gallery
   * filter. This is deliberately unrelated to option availability.
   */
  document.addEventListener('theme:variant:change', function (event) {
    if (!event.detail || !event.detail.variant) return;

    var root = getProductRoot(event.target);
    if (!root) return;

    var instance = instances.get(root);
    if (!instance) return;

    var filter = getFilterForVariant(instance, event.detail.variant.id);

    window.requestAnimationFrame(function () {
      showFilter(instance, filter);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Quick view / dynamically inserted product sections. */
  var observer = new MutationObserver(function () {
    initAll();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
